unit Sphinx.OidcClient;

{$I Sphinx.inc}

interface

uses
  SysUtils, DateUtils, StrUtils,
{$IFDEF PAS2JS}
  JS, Web,
  Sphinx.Web.Http,
{$ELSE}
  Generics.Collections,
  Rtti,
  Bcl.Json,
  Bcl.Json.Classes,
  Sparkle.Http.Client,
{$ENDIF}
  Bcl.Json.Common,
  Bcl.Utils,
  Sparkle.Utils,
  Sphinx.Discovery.Metadata,
  Sphinx.OidcClient.AuthRequest,
  Sphinx.OidcClient.AuthState,
  Sphinx.OidcClient.AuthResult,
  Sphinx.OidcClient.AuthResultEntry,
  Sphinx.OidcClient.Profile,
  Sphinx.OidcClient.Storage;

type
  TAuthState = Sphinx.OidcClient.AuthState.TAuthState;
  TAuthResult = Sphinx.OidcClient.AuthResult.TAuthResult;
  ITokenResult = Sphinx.OidcClient.AuthResult.ITokenResult;

  /// <summary>
  /// Base exception class for all OIDC client errors.
  /// </summary>
  EOidcClientException = class(Exception);

  /// <summary>
  /// Exception raised when the authorization server returns an error response.
  /// </summary>
  /// <remarks>
  /// Raised during authorization or token flows when the server response contains
  /// an <c>error</c> field. The <c>Error</c> and <c>ErrorDescription</c> values
  /// from the server response are combined into the exception message.
  /// </remarks>
  EOidcClientError = class(EOidcClientException)
  public
    /// <summary>
    /// Creates an <c>EOidcClientError</c> from an error code and description.
    /// </summary>
    /// <param name="Error">The OAuth2/OIDC error code returned by the server.</param>
    /// <param name="ErrorDescription">The human-readable description of the error.</param>
    constructor Create(const Error, ErrorDescription: string); overload;
  end;

  /// <summary>
  /// Holds the raw parameters returned by the authorization server in a redirect callback.
  /// </summary>
  /// <remarks>
  /// This class captures the URL parameters delivered to the redirect URI after an
  /// authorization request. Depending on the response type and mode, the server may
  /// populate <c>Code</c> (authorization code flow), <c>AccessToken</c> (implicit flow),
  /// <c>IdToken</c>, or <c>Error</c> and <c>ErrorDescription</c> when the request failed.
  /// </remarks>
  TClientAuthResponse = class
  private
    FState: string;
    FError: string;
    FErrorDescription: string;
    FIdToken: string;
    FCode: string;
    FAccessToken: string;
    FTokenType: string;
    FExpiresIn: Integer;
    FExpiration: TDateTime;
    FScope: string;
  public
    /// <summary>Gets or sets the <c>state</c> parameter echoed back by the server.</summary>
    property State: string read FState write FState;
    /// <summary>Gets or sets the identity token returned in implicit or hybrid flows.</summary>
    property IdToken: string read FIdToken write FIdToken;
    /// <summary>Gets or sets the authorization code returned in code flow responses.</summary>
    property Code: string read FCode write FCode;
    /// <summary>Gets or sets the access token returned in implicit flow responses.</summary>
    property AccessToken: string read FAccessToken write FAccessToken;
    /// <summary>Gets or sets the token type, typically <c>Bearer</c>.</summary>
    property TokenType: string read FTokenType write FTokenType;
    /// <summary>Gets or sets the lifetime of the access token in seconds.</summary>
    property ExpiresIn: Integer read FExpiresIn write FExpiresIn;
    /// <summary>Gets or sets the space-separated list of scopes granted by the server.</summary>
    property Scope: string read FScope write FScope;
    /// <summary>Gets the absolute expiration date and time of the access token.</summary>
    property Expiration: TDateTime read FExpiration;
    /// <summary>Gets or sets the OAuth2 error code when the server returned an error.</summary>
    property Error: string read FError write FError;
    /// <summary>Gets or sets the human-readable description of the error.</summary>
    property ErrorDescription: string read FErrorDescription write FErrorDescription;
  end;

  /// <summary>
  /// Holds the endpoint URLs for an OpenID Connect provider.
  /// </summary>
  /// <remarks>
  /// When <c>AutoDiscover</c> is enabled on <c>TOidcClient</c>, these values are populated
  /// automatically from the provider's discovery document. When <c>AutoDiscover</c> is
  /// disabled, they must be set manually via the <c>ProviderInfo</c> property before
  /// starting an authorization flow.
  /// </remarks>
  TProviderInformation = class
  private
    FIssuer: string;
    FAuthorizationEndpoint: string;
    FTokenEndpoint: string;
    FEndSessionEndpoint: string;
  public
    /// <summary>Gets or sets the issuer identifier of the OpenID Connect provider.</summary>
    property Issuer: string read FIssuer write FIssuer;
    /// <summary>Gets or sets the URL of the authorization endpoint.</summary>
    property AuthorizationEndpoint: string read FAuthorizationEndpoint write FAuthorizationEndpoint;
    /// <summary>Gets or sets the URL of the token endpoint.</summary>
    property TokenEndpoint: string read FTokenEndpoint write FTokenEndpoint;
    /// <summary>Gets or sets the URL of the end-session (logout) endpoint.</summary>
    property EndSessionEndpoint: string read FEndSessionEndpoint write FEndSessionEndpoint;
  end;

{$IFNDEF PAS2JS}
  /// <summary>
  /// Callback invoked after an <c>THttpClient</c> instance is created, allowing
  /// custom configuration before it is used to make requests.
  /// </summary>
  /// <remarks>
  /// <c>Client</c>: The newly created <c>THttpClient</c> instance to configure.
  /// Use this callback to set headers, timeouts, TLS settings, or proxy options.
  /// Assign an implementation to <c>TOidcClient.OnHttpClientCreate</c>.
  /// </remarks>
  THttpClientProc = reference to procedure(Client: THttpClient);
{$ENDIF}

  /// <summary>
  /// Callback for custom issuer validation during ID token processing.
  /// </summary>
  /// <remarks>
  /// <c>Issuer</c>: The actual issuer value found in the token's <c>iss</c> claim.
  /// <c>ExpectedIssuer</c>: The expected issuer value, with any <c>{tenantid}</c> placeholder
  /// already resolved using the token's tenant id claim.
  /// <c>Valid</c>: Set to <c>False</c> to reject the token. When <c>SkipIssuerValidation</c> is
  /// <c>False</c>, this parameter is pre-initialized to the result of the standard string comparison,
  /// so the callback can override the default decision in either direction. When
  /// <c>SkipIssuerValidation</c> is <c>True</c>, this parameter is pre-initialized to <c>True</c>.
  /// Raising an exception inside the callback also causes token validation to fail.
  /// </remarks>
  TValidateIssuerProc = reference to procedure(const Issuer, ExpectedIssuer: string; var Valid: Boolean);

  /// <summary>
  /// Client for performing OpenID Connect (OIDC) and OAuth2 authorization flows.
  /// </summary>
  /// <remarks>
  /// <c>TOidcClient</c> implements the authorization code flow with PKCE, implicit flow,
  /// client credentials flow, and token refresh against any standards-compliant OIDC
  /// provider. Configure <c>Authority</c>, <c>ClientId</c>, <c>Scope</c>, and
  /// <c>RedirectUri</c>, then call <c>StartAuthorize</c> to begin the flow and
  /// <c>FinishAuthorize</c> once the browser returns the callback URL.
  /// Set <c>AutoDiscover</c> to <c>True</c> to retrieve endpoint URLs automatically from
  /// the provider's discovery document; otherwise populate <c>ProviderInfo</c> manually.
  /// </remarks>
  TOidcClient = class
  strict private
    FStorage: TClientStorage;
    FClientId: string;
    FClientSecret: string;
    FScope: string;
    FRedirectUri: string;
    FProviderInfo: TProviderInformation;
    FValidProviderInfo: TProviderInformation;
    FAuthority: string;
    FAutoDiscover: Boolean;
    FSkipIssuerValidation: Boolean;
    FOnValidateIssuer: TValidateIssuerProc;
{$IFNDEF PAS2JS}
    FOnHttpClientCreate: THttpClientProc;
{$ENDIF}
    procedure SetAuthority(const Value: string);
    function AuthStateKey: string;
    function IsCallbackUri(const ResponseUri, ExpectedState: string): Boolean;
    function ResponseFromParams(Params: TQueryString): TClientAuthResponse;
    procedure ValidateIdToken(Entry: TAuthResultEntry; AuthState: TAuthState);
    procedure ValidateAccessToken(Response: TClientAuthResponse; AuthState: TAuthState);
    procedure ValidateResponse(Entry: TAuthResultEntry; Response: TClientAuthResponse; AuthState: TAuthState); {$IFDEF PAS2JS}async;{$ENDIF}
    procedure ProcessCodeResponse(Entry: TAuthResultEntry; Response: TClientAuthResponse; AuthState: TAuthState); {$IFDEF PAS2JS}async;{$ENDIF}
    procedure ValidateIdTokenJwt(Payload: TJObject; const Issuer, Audience: string);
    function ProcessAuthResponse(const CallbackUrl: string): TAuthResult; {$IFDEF PAS2JS}async;{$ENDIF}
    function HttpRequestToken(const Params, AuthHeader: string): TJObject; {$IFDEF PAS2JS}async;{$ENDIF}
    procedure ReadTokenResponse(TokenResponse: TJObject; Entry: TAuthResultEntry; const AScope: string);
    procedure RequestTokenEntry(Entry: TAuthResultEntry; const AScope: string); {$IFDEF PAS2JS}async;{$ENDIF}
    procedure RefreshTokenEntry(Entry: TAuthResultEntry; const ARefreshToken, AScope: string); {$IFDEF PAS2JS}async;{$ENDIF}
    function ExpiresInFromString(const ExpiresInParam: string): Integer;
    procedure CheckProviderInformation; {$IFDEF PAS2JS}async;{$ENDIF}
    procedure MetadataToProvider(Metadata: TOidcProviderMetadata; Info: TProviderInformation);
    function GetJsonProviderMetadata(const Url: string): TJObject; {$IFDEF PAS2JS}async;{$ENDIF}
    function RemoveTrailingSlash(const S: string): string;
{$IFNDEF PAS2JS}
    function CreateHttpClient: THttpClient;
{$ENDIF}
  strict protected
    property ValidProviderInfo: TProviderInformation read FValidProviderInfo;
  protected
    function GetProviderMetadata: TOidcProviderMetadata; {$IFDEF PAS2JS}async;{$ENDIF}
    /// <summary>
    /// Validates a token's issuer claim against the expected issuer, resolving any
    /// <c>{tenantid}</c> placeholder with the supplied tenant id before comparing.
    /// </summary>
    /// <remarks>
    /// Exposed for testing. Honors <c>SkipIssuerValidation</c> and <c>OnValidateIssuer</c>,
    /// and raises <c>EOidcClientException</c> when the issuer is rejected.
    /// </remarks>
    procedure DoValidateIssuer(const ExpectedIssuer, TokenIssuer, TenantId: string);
  public
    /// <summary>
    /// Creates a new <c>TOidcClient</c> instance with default settings.
    /// </summary>
    constructor Create;
    destructor Destroy; override;

    /// <summary>
    /// Begins an authorization flow and returns the state object needed to complete it.
    /// </summary>
    /// <param name="AppState">
    /// Optional application-defined string that is preserved through the flow and returned in
    /// <c>TAuthResult.AppState</c>. Use this to restore navigation state after the redirect.
    /// </param>
    /// <returns>
    /// A <c>TAuthState</c> instance containing the authorization URL and the state parameters
    /// required to validate the callback. The caller is responsible for redirecting the user
    /// to <c>TAuthState.AuthorizeUrl</c> and for keeping the returned object available until
    /// <c>FinishAuthorize</c> is called.
    /// </returns>
    /// <remarks>
    /// Calls <c>CheckProviderInformation</c> internally, which may fetch the discovery document
    /// when <c>AutoDiscover</c> is <c>True</c>. The generated state is persisted via the internal
    /// <c>TClientStorage</c> so that <c>FinishAuthorize</c> can retrieve it.
    /// </remarks>
    function StartAuthorize(const AppState: string = ''; const APrompt: string = ''): TAuthState; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    /// Completes the authorization flow by processing the callback URL returned by the provider.
    /// </summary>
    /// <param name="CallbackUrl">
    /// The full redirect URL delivered to the application after the user authenticated,
    /// including all query or fragment parameters appended by the server.
    /// </param>
    /// <returns>
    /// A <c>TAuthResult</c> containing the tokens obtained from the provider.
    /// The caller is responsible for destroying the returned instance.
    /// </returns>
    /// <remarks>
    /// For authorization code flows, this method exchanges the received code for tokens by
    /// calling the token endpoint. Raises <c>EOidcClientError</c> if the server returned an
    /// error, or <c>EOidcClientException</c> if the state cannot be matched or token validation
    /// fails.
    /// </remarks>
    function FinishAuthorize(const CallbackUrl: string): TAuthResult; overload; {$IFDEF PAS2JS}async;{$ENDIF}
{$IFDEF PAS2JS}
    /// <summary>
    /// Completes the authorization flow by reading the current browser URL as the callback.
    /// </summary>
    /// <returns>
    /// A <c>TAuthResult</c> containing the tokens obtained from the provider.
    /// </returns>
    function FinishAuthorize: TAuthResult; overload; {$IFDEF PAS2JS}async;{$ENDIF}
{$ENDIF}

    /// <summary>
    /// Returns <c>True</c> if the given URL is a valid authorization callback for a pending flow.
    /// </summary>
    /// <param name="CallbackUrl">The URL to test.</param>
    /// <returns>
    /// <c>True</c> if the URL matches the configured <c>RedirectUri</c> and contains a
    /// recognized <c>state</c> parameter; <c>False</c> otherwise.
    /// </returns>
    function IsValidCallback(const CallbackUrl: string): Boolean; overload;
{$IFDEF PAS2JS}
    /// <summary>
    /// Returns <c>True</c> if the current browser URL is a valid authorization callback for a pending flow.
    /// </summary>
    /// <returns>
    /// <c>True</c> if the current URL matches the configured <c>RedirectUri</c> and contains a
    /// recognized <c>state</c> parameter; <c>False</c> otherwise.
    /// </returns>
    function IsValidCallback: Boolean; overload;
{$ENDIF}

//    /// <summary>
//    /// Builds a <c>TAuthState</c> from an explicit authorization request object.
//    /// </summary>
//    /// <param name="Request">
//    /// A <c>TClientAuthRequest</c> describing the authorization parameters such as response
//    /// type, scope, redirect URI, and PKCE challenge.
//    /// </param>
//    /// <param name="AppState">
//    /// Optional application-defined string preserved through the flow.
//    /// </param>
//    /// <returns>
//    /// A <c>TAuthState</c> instance ready for use with <c>FinishAuthorize</c>.
//    /// </returns>
//    /// <remarks>
//    /// Use this overload when you need full control over the authorization request parameters.
//    /// <c>StartAuthorize</c> builds a default request internally; <c>CreateState</c> lets you
//    /// supply a custom one.
//    /// </remarks>
    function CreateState(Request: TClientAuthRequest; const AppState: string = ''): TAuthState; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    /// Injects a previously created authorization state into this client so that a subsequent
    /// <c>FinishAuthorize</c> call can complete the flow.
    /// </summary>
    /// <param name="AState">
    /// The authorization state to restore. The client takes ownership of the instance.
    /// </param>
    /// <remarks>
    /// Use this method to complete an authorization flow on a different <c>TOidcClient</c> instance
    /// than the one that started it -- for example, when the challenge and the callback are handled by
    /// two separate server requests. The auth state is keyed by <c>ClientId</c>, so this client must be
    /// configured with the same <c>ClientId</c> that produced <c>AState</c> for the callback to match.
    /// Any auth state previously stored under the same key is replaced and freed.
    /// </remarks>
    procedure RestoreState(AState: TAuthState);

    /// <summary>
    /// Requests an access token using the client credentials flow.
    /// </summary>
    /// <returns>
    /// An <c>ITokenResult</c> containing the access token granted by the server.
    /// </returns>
    /// <remarks>
    /// Uses <c>ClientId</c> and <c>ClientSecret</c> to authenticate directly with the token
    /// endpoint. No user interaction or redirect is involved. This flow is intended for
    /// machine-to-machine communication where the client acts on its own behalf.
    /// </remarks>
    function RequestToken: ITokenResult; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    /// Builds the end-session URL for logging out the user at the identity provider.
    /// </summary>
    /// <param name="IdTokenHint">
    /// Optional identity token previously received from the provider. Supplying this hint
    /// allows the provider to identify the session to terminate.
    /// </param>
    /// <param name="PostLogoutRedirectUri">
    /// Optional URI to which the provider should redirect the user after logout completes.
    /// </param>
    /// <returns>The fully formed end-session URL to redirect the user to.</returns>
    /// <remarks>
    /// Calls <c>CheckProviderInformation</c> to ensure the end-session endpoint is known.
    /// Raises <c>EOidcClientException</c> if the provider does not expose an end-session endpoint.
    /// </remarks>
    function BuildLogoutUrl(const IdTokenHint: string = '';
      const PostLogoutRedirectUri: string = ''): string;  {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    /// Exchanges a refresh token for a new set of tokens.
    /// </summary>
    /// <param name="ARefreshToken">The refresh token to exchange.</param>
    /// <param name="AScope">
    /// Optional scope to request. If empty, the server grants the same scope as the original
    /// authorization. The requested scope cannot exceed the scope of the original grant.
    /// </param>
    /// <returns>
    /// An <c>TAuthResult</c> containing the new access token, a new refresh token (if the server
    /// implements token rotation), and optionally a new identity token.
    /// The caller is responsible for destroying the returned <c>TAuthResult</c> instance.
    /// </returns>
    /// <remarks>
    /// The caller is responsible for supplying a valid refresh token. The server endpoint and
    /// client authentication are derived from the current <c>Authority</c> and <c>ClientId</c>
    /// settings, so <c>CheckProviderInformation</c> is called automatically before the request.
    /// If the server issues a new refresh token in the response (token rotation), it replaces
    /// the value in the returned result. If the server omits the refresh token, the original
    /// <c>ARefreshToken</c> value is preserved in the result.
    /// </remarks>
    function RefreshTokens(const ARefreshToken: string; const AScope: string = ''): TAuthResult; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    /// Gets or sets the base URL of the OpenID Connect provider (the issuer).
    /// </summary>
    /// <remarks>
    /// When <c>AutoDiscover</c> is <c>True</c>, the discovery document is fetched from
    /// <c>Authority + "/.well-known/openid-configuration"</c>. The value must not end
    /// with a trailing slash; any trailing slash is removed automatically on assignment.
    /// </remarks>
    property Authority: string read FAuthority write SetAuthority;

    /// <summary>Gets or sets the client identifier registered with the identity provider.</summary>
    property ClientId: string read FClientId write FClientId;

    /// <summary>
    /// Gets or sets the client secret used to authenticate with the token endpoint.
    /// </summary>
    /// <remarks>
    /// Required for confidential clients (e.g., the client credentials flow or code flow
    /// without PKCE). Leave empty for public clients that rely on PKCE instead.
    /// </remarks>
    property ClientSecret: string read FClientSecret write FClientSecret;

    /// <summary>
    /// Gets or sets the space-separated list of scopes to request during authorization.
    /// </summary>
    /// <remarks>
    /// Include <c>openid</c> to trigger an OpenID Connect flow and receive an identity token.
    /// Additional scopes such as <c>profile</c>, <c>email</c>, or custom API scopes can be
    /// appended.
    /// </remarks>
    property Scope: string read FScope write FScope;

    /// <summary>
    /// Gets or sets the URI to which the provider redirects the user after authorization.
    /// </summary>
    /// <remarks>
    /// Must match one of the redirect URIs registered for <c>ClientId</c> at the provider.
    /// Pass the full callback URL received at this URI to <c>FinishAuthorize</c>.
    /// </remarks>
    property RedirectUri: string read FRedirectUri write FRedirectUri;

    /// <summary>
    /// Gets or sets whether endpoint URLs are discovered automatically from the provider metadata.
    /// </summary>
    /// <remarks>
    /// When <c>True</c> (the default), <c>TOidcClient</c> fetches
    /// <c>Authority + "/.well-known/openid-configuration"</c> before the first request to
    /// populate endpoint URLs. When <c>False</c>, you must populate <c>ProviderInfo</c>
    /// manually before starting any flow.
    /// </remarks>
    property AutoDiscover: Boolean read FAutoDiscover write FAutoDiscover;

    /// <summary>
    /// Gets or sets whether issuer validation is skipped during ID token processing.
    /// </summary>
    /// <remarks>
    /// When <c>False</c> (the default), the <c>iss</c> claim in the identity token must exactly
    /// match the <c>Authority</c> value, and the issuer returned by the OpenID Connect discovery
    /// document must also match <c>Authority</c>.
    /// For multi-tenant identity providers -- for example, Microsoft Entra using the
    /// <c>organizations</c> or <c>common</c> authority -- the issuer is a template containing a
    /// <c>{tenantid}</c> placeholder (e.g. <c>https://login.microsoftonline.com/{tenantid}/v2.0</c>).
    /// This placeholder is resolved automatically with the token's tenant id (<c>tid</c>) claim
    /// before validation, so issuer validation can be kept enabled for those providers and this
    /// property left <c>False</c>. Set to <c>True</c> only as a last resort to disable issuer
    /// validation entirely, or use <see cref="OnValidateIssuer">OnValidateIssuer</see> to supply
    /// custom validation logic in place of the standard check.
    /// </remarks>
    property SkipIssuerValidation: Boolean read FSkipIssuerValidation write FSkipIssuerValidation;

    /// <summary>
    /// Gets or sets the callback invoked to validate the issuer claim during ID token processing.
    /// </summary>
    /// <remarks>
    /// When assigned, this callback fires on every ID token validation regardless of the value of
    /// <see cref="SkipIssuerValidation">SkipIssuerValidation</see>. The <c>Valid</c> parameter
    /// is pre-initialized to the result of the standard issuer comparison (or to <c>True</c> when
    /// <c>SkipIssuerValidation</c> is <c>True</c>), so the callback can either accept the default
    /// decision or override it. Setting <c>Valid</c> to <c>False</c> or raising an exception
    /// causes token validation to fail. See <c>TValidateIssuerProc</c> for the callback signature.
    /// </remarks>
    property OnValidateIssuer: TValidateIssuerProc read FOnValidateIssuer write FOnValidateIssuer;
    /// <summary>
    /// Gets the provider information object that holds the endpoint URLs used by this client.
    /// </summary>
    /// <remarks>
    /// When <c>AutoDiscover</c> is <c>False</c>, set <c>ProviderInfo.AuthorizationEndpoint</c>,
    /// <c>ProviderInfo.TokenEndpoint</c>, and optionally <c>ProviderInfo.EndSessionEndpoint</c>
    /// before starting any flow.
    /// </remarks>
    property ProviderInfo: TProviderInformation read FProviderInfo;
{$IFNDEF PAS2JS}
    /// <summary>
    /// Gets or sets the callback invoked each time an <c>THttpClient</c> instance is created.
    /// </summary>
    /// <remarks>
    /// Assign this property to customize the HTTP client before it sends any request -- for
    /// example, to configure a proxy, set custom headers, or adjust TLS settings.
    /// See <c>THttpClientProc</c> for the callback signature.
    /// </remarks>
    property OnHttpClientCreate: THttpClientProc read FOnHttpClientCreate write FOnHttpClientCreate;
{$ENDIF}
  end;

implementation

uses
  Bcl.Lang,
  Sphinx.Consts,
  Sphinx.Utils;

const
  // Placeholder found in the issuer of multi-tenant providers (e.g. Microsoft Entra
  // returns "https://login.microsoftonline.com/{tenantid}/v2.0" in its discovery
  // document). It is resolved with the token's tenant id claim before validation.
  IssuerTenantPlaceholder = '{tenantid}';

{ TOidcClient }

procedure TOidcClient.CheckProviderInformation;
var
  Metadata: TOidcProviderMetadata;
begin
  if Authority = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), ['Authority']);
  if ClientId = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), ['ClientId']);

  if FValidProviderInfo = nil then
  begin
    FValidProviderInfo := TProviderInformation.Create;
    if AutoDiscover then
    begin
      Metadata := {$IFDEF PAS2JS}await{$ENDIF}(GetProviderMetadata);
      try
        if Metadata <> nil then
          MetadataToProvider(Metadata, FValidProviderInfo);
      finally
{$IFNDEF PAS2JS}
        Metadata.Free;
{$ENDIF}
      end;
    end;

    // Load custom provider data
    if ProviderInfo <> nil then
    begin
      if FValidProviderInfo.Issuer = '' then
        FValidProviderInfo.Issuer := ProviderInfo.Issuer;
      if FValidProviderInfo.AuthorizationEndpoint = '' then
        FValidProviderInfo.AuthorizationEndpoint := ProviderInfo.AuthorizationEndpoint;
      if FValidProviderInfo.TokenEndpoint = '' then
        FValidProviderInfo.FTokenEndpoint := ProviderInfo.TokenEndpoint;
      if FValidProviderInfo.EndSessionEndpoint = '' then
        FValidProviderInfo.EndSessionEndpoint := ProviderInfo.EndSessionEndpoint;
    end;

    if FValidProviderInfo.Issuer = '' then
      FValidProviderInfo.Issuer := Authority;
  end;

  if FValidProviderInfo.Issuer = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), ['Issuer']);
  // A templated issuer (e.g. the {tenantid} placeholder used by multi-tenant Microsoft
  // Entra) legitimately differs from the authority, so skip the equality check for it;
  // the per-token issuer is still validated in DoValidateIssuer after resolving the
  // placeholder with the token's tenant id.
  if not FSkipIssuerValidation and not ContainsText(FValidProviderInfo.Issuer, IssuerTenantPlaceholder) then
    if RemoveTrailingSlash(FValidProviderInfo.Issuer) <> RemoveTrailingSlash(Authority) then
      raise EOidcClientException.CreateFmt(_(SInvalidParameter), ['Issuer', FValidProviderInfo.Issuer]);
end;

constructor TOidcClient.Create;
begin
  inherited Create;
  FStorage := TClientStorage.Create;
  FProviderInfo := TProviderInformation.Create;
  FAutoDiscover := True;
end;

{$IFNDEF PAS2JS}
function TOidcClient.CreateHttpClient: THttpClient;
begin
  Result := THttpClient.Create;
  try
    if Assigned(FOnHttpClientCreate) then
      FOnHttpClientCreate(Result);
  except
    Result.Free;
    raise;
  end;
end;
{$ENDIF}

function TOidcClient.CreateState(Request: TClientAuthRequest; const AppState: string = ''): TAuthState;
var
  AuthState: TAuthState;
  AuthorizeUrl: string;
begin
{$IFDEF PAS2JS}
  await(CheckProviderInformation);
{$ELSE}
  CheckProviderInformation;
{$ENDIF}
  if FValidProviderInfo.AuthorizationEndpoint = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), ['AuthorizationEndpoint']);
  if Request.ResponseType = ResponseTypes.Code then
    if FValidProviderInfo.TokenEndpoint = '' then
      raise EOidcClientException.CreateFmt(_(SMissingParameter), ['TokenEndpoint']);

  // Create needed random strings (state, nonce, code verifier...)
  if Request.State = '' then
    Request.State := TBclUtils.RandomString;
  if Request.IsOpenId and (Request.Nonce = '') then
    Request.Nonce := TBclUtils.RandomString;
  if Request.IsCodeFlow then
  begin
    if Request.CodeVerifier = '' then
      Request.CodeVerifier := TBclUtils.RandomString(96);
    if Request.CodeChallenge = '' then
      Request.CodeChallenge := Sha256(Request.CodeVerifier);
  end
  else
  begin
    Request.CodeVerifier := '';
    Request.CodeChallenge := '';
  end;
  AuthorizeUrl := Request.GetRequestUrl(ValidProviderInfo.AuthorizationEndpoint, ClientId);

  // Create state object and save it in storage
{$IFDEF PAS2JS}
  AuthState := TAuthState.new;
{$ELSE}
  AuthState := TAuthState.Create;
{$ENDIF}
  try
    AuthState.AuthorizeUrl := AuthorizeUrl;
    AuthState.State := Request.State;
    AuthState.Scope := Request.Scope;
    AuthState.ClientId := ClientId;
    AuthState.RedirectUri := Request.RedirectUri;
    AuthState.Nonce := Request.Nonce;
    AuthState.CreatedOn := Now;
    AuthState.ResponseMode := Request.ResponseMode;
    AuthState.CodeVerifier := Request.CodeVerifier;
    AuthState.CodeChallenge := Request.CodeChallenge;
    AuthState.AppState := AppState;
    FStorage.Save<TAuthState>(AuthStateKey, AuthState);
  except
{$IFNDEF PAS2JS}
    AuthState.Free;
{$ENDIF}
    raise;
  end;
  Result := AuthState;
end;

procedure TOidcClient.RestoreState(AState: TAuthState);
begin
  FStorage.Save<TAuthState>(AuthStateKey, AState);
end;

destructor TOidcClient.Destroy;
begin
  FStorage.Free;
  FValidProviderInfo.Free;
  FProviderInfo.Free;
  inherited;
end;

function TOidcClient.ExpiresInFromString(const ExpiresInParam: string): Integer;
begin
  if ExpiresInParam = '' then
    Result := 0
  else
    if not TryStrToInt(ExpiresInParam, Result) then
      raise EOidcClientException.CreateFmt(_(SInvalidParameter), [TokenResponseParams.ExpiresIn, ExpiresInParam]);
end;

function TOidcClient.FinishAuthorize(const CallbackUrl: string): TAuthResult;
begin
{$IFDEF PAS2JS}
  Result := await(ProcessAuthResponse(CallbackUrl));
{$ELSE}
  Result := ProcessAuthResponse(CallbackUrl);
{$ENDIF}
end;

function TOidcClient.GetJsonProviderMetadata(const Url: string): TJObject;
{$IFDEF PAS2JS}
var
  Request: IHttpRequest;
  Response: THttpResponse;
begin
  Result := nil;
  Request := THttpRequest.Create;
  Request.Uri := Url;
  Request.Method := 'GET';
  try
    Response := await(SendHttpRequestAsync(Request));
    if Response.StatusCode = 200 then
      Result := TJSJson.parseObject(Response.ContentAsText);
  except
    on E: Exception do
      console.log(E);
  end;
end;
{$ELSE}
var
  Client: THttpClient;
  Response: THttpResponse;
begin
  Result := nil;
  Client := CreateHttpClient;
  try
    Response := Client.Get(Url);
    try
      if Response.StatusCode = 200 then
        Result := TJson.Deserialize<TJObject>(TEncoding.UTF8.GetString(Response.ContentAsBytes))
    finally
      Response.Free;
    end;
  finally
    Client.Free;
  end;
end;
{$ENDIF}

function TOidcClient.GetProviderMetadata: TOidcProviderMetadata;
var
  Url: string;
  JObject: TJObject;
begin
  Url := TSparkleUtils.CombineUrlFast(Authority, '.well-known/openid-configuration');
  JObject := {$IFDEF PAS2JS}await{$ENDIF}(GetJsonProviderMetadata(Url));
  try
    Result := TOidcProviderMetadata.Create;
    try
      if JObject <> nil then
        MetadataFromJObject(Result, JObject);
    except
      Result.Free;
      raise;
    end;
  finally
{$IFNDEF PAS2JS}
    JObject.Free;
{$ENDIF}
  end;
end;

function TOidcClient.HttpRequestToken(const Params, AuthHeader: string): TJObject;
{$IFDEF PAS2JS}
var
  Request: IHttpRequest;
  Response: THttpResponse;
  ContentType: string;
begin
  await(CheckProviderInformation);
  if FValidProviderInfo.TokenEndpoint = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), ['TokenEndpoint']);

  Request := THttpRequest.Create;
  Request.Uri := ValidProviderInfo.TokenEndpoint;
  Request.Method := 'POST';
  Request.Headers.AddValue('Content-Type', 'application/x-www-form-urlencoded');
  if AuthHeader <> '' then
    Request.Headers.AddValue('Authorization', AuthHeader);
  Request.Content := Params;

  Response := await(SendHttpRequestAsync(Request));
  ContentType := Response.Headers.Get('Content-Type');
  if SameText(ContentType, 'application/json') or StartsText('application/json;', ContentType) then
    Result := TJSJson.parseObject(Response.ContentAsText)
  else
    Result := nil;
end;
{$ELSE}
var
  Client: THttpClient;
  Request: THttpRequest;
  Response: THttpResponse;
begin
  CheckProviderInformation;
  Client := CreateHttpClient;
  try
    Request := THttpRequest.Create;
    try
      Request.Uri := ValidProviderInfo.TokenEndpoint;
      Request.Method := 'POST';
      Request.Headers.AddValue('Content-Type', 'application/x-www-form-urlencoded');

      // while OAuth compliant servers should always return JSON responses, some servers (like GitHub) do not do that
      // for backward compatbility. Force the JSON response
      Request.Headers.AddValue('Accept', 'application/json');
      if AuthHeader <> '' then
        Request.Headers.AddValue('Authorization', AuthHeader);
      Request.SetContent(TEncoding.UTF8.GetBytes(Params));

      Response := Client.Send(Request);
      try
        if SameText(Response.ContentType, 'application/json') or StartsText('application/json;', Response.ContentType) then
          Result := TJson.Deserialize<TJObject>(TEncoding.UTF8.GetString(Response.ContentAsBytes))
        else
          Result := nil;
      finally
        Response.Free;
      end;
    finally
      Request.Free;
    end;
  finally
    Client.Free;
  end;
end;
{$ENDIF}

{$IFDEF PAS2JS}
function TOidcClient.FinishAuthorize: TAuthResult;
var
  CallbackUrl: string;
begin
  CallbackUrl := window.location.href;
  Result := await(ProcessAuthResponse(CallbackUrl));
end;
{$ENDIF}

function TOidcClient.StartAuthorize(const AppState: string = ''; const APrompt: string = ''): TAuthState;
var
  Request: TClientAuthRequest;
begin
  Request := TClientAuthRequest.Create;
  try
    Request.RedirectUri := RedirectUri;
    Request.Scope := Scope;
    Request.ResponseType := 'code';
    Request.Prompt := APrompt;
{$IFDEF PAS2JS}
    Result := await(CreateState(Request, AppState));
{$ELSE}
    Result := CreateState(Request, AppState);
{$ENDIF}
  finally
    Request.Free;
  end;
end;

function TOidcClient.IsCallbackUri(const ResponseUri, ExpectedState: string): Boolean;
var
  Params: TQueryString;
begin
  Params := ParamsFromUri(ResponseUri);
  if Params.GetValue(AuthorizeResponseParams.State) = '' then
    Exit(False);
  if (Params.GetValue(AuthorizeResponseParams.AccessToken) = '')
    and (Params.GetValue(AuthorizeResponseParams.Error) = '')
    and (Params.GetValue(AuthorizeResponseParams.IdentityToken) = '')
    and (Params.GetValue(AuthorizeResponseParams.Code) = '') then
    Exit(False);
  Result := (ExpectedState = '') or (ExpectedState = Params.GetValue(AuthorizeResponseParams.State));
end;

function TOidcClient.IsValidCallback(const CallbackUrl: string): Boolean;
var
  AuthState: TAuthState;
begin
  AuthState := FStorage.Get<TAuthState>(AuthStateKey);
  if AuthState = nil then
    Exit(False);
  Result := IsCallbackUri(CallbackUrl, AuthState.State);
end;

procedure TOidcClient.MetadataToProvider(Metadata: TOidcProviderMetadata; Info: TProviderInformation);
begin
  Info.Issuer := Metadata.Issuer;
  Info.AuthorizationEndpoint := Metadata.AuthorizationEndpoint;
  Info.TokenEndpoint := Metadata.TokenEndpoint;
  Info.EndSessionEndpoint := Metadata.EndSessionEndpoint;
end;

function TOidcClient.BuildLogoutUrl(const IdTokenHint: string = '';
  const PostLogoutRedirectUri: string = ''): string;
var
  Params: string;
begin
{$IFDEF PAS2JS}
  await(CheckProviderInformation);
{$ELSE}
  CheckProviderInformation;
{$ENDIF}
  if ValidProviderInfo.EndSessionEndpoint = '' then
    Exit('');

  Params := '';
  if IdTokenHint <> '' then
    Params := Params + '&id_token_hint=' + TBclUtils.PercentEncode(IdTokenHint);
  if PostLogoutRedirectUri <> '' then
    Params := Params + '&post_logout_redirect_uri=' + TBclUtils.PercentEncode(PostLogoutRedirectUri);

  if Params = '' then
    Result := ValidProviderInfo.EndSessionEndpoint
  else
    Result := ValidProviderInfo.EndSessionEndpoint + '?' + Copy(Params, 2, MaxInt);
end;

{$IFDEF PAS2JS}
function TOidcClient.IsValidCallback: Boolean;
begin
  Result := IsValidCallback(window.location.href);
end;
{$ENDIF}

procedure TOidcClient.ProcessCodeResponse(Entry: TAuthResultEntry; Response: TClientAuthResponse; AuthState: TAuthState);
var
  Params: IModifiableParams;
  AuthHeader: string;
  RequestParams: string;
  TokenResponse: TJObject;
  Error: string;
  ExpiresIn: Integer;
begin
  if (AuthState.CodeVerifier = '') then
    raise EOidcClientException.Create(Format(_(SMissingParameter), [TokenRequestParams.CodeVerifier]));

  // build request params
  Params := TModifiableParams.Create;
  Params.Add(TokenRequestParams.GrantType, GrantTypes.AuthorizationCode);
  Params.Add(TokenRequestParams.Code, Response.Code);
  Params.Add(TokenRequestParams.RedirectUri, AuthState.RedirectUri);
  Params.Add(TokenRequestParams.CodeVerifier, AuthState.CodeVerifier);
  if Self.ClientSecret = '' then // no authentication
  begin
    Params.Add(AuthorizeRequestParams.ClientId, AuthState.ClientId);
    AuthHeader := '';
  end
  else
    AuthHeader := TSparkleUtils.BasicAuthHeaderValue(AuthState.ClientId, Self.ClientSecret);
  RequestParams := BuildQueryParams(Params);

{$IFDEF PAS2JS}
  TokenResponse := await(HttpRequestToken(RequestParams, AuthHeader));
{$ELSE}
  TokenResponse := HttpRequestToken(RequestParams, AuthHeader);
{$ENDIF}
  try
    if TokenResponse = nil then
      raise EOidcClientException.Create('Could not retrieve token');

    Error := JStringOrDefault(TokenResponse[TokenResponseParams.Error]);
    if Error <> '' then
      raise EOidcClientError.Create(Error, JStringOrDefault(TokenResponse[TokenResponseParams.ErrorDescription]));

    Entry.AccessToken := JStringOrDefault(TokenResponse[TokenResponseParams.AccessToken]);
    Entry.RefreshToken := JStringOrDefault(TokenResponse[TokenResponseParams.RefreshToken]);
    Entry.IdToken := JStringOrDefault(TokenResponse[TokenResponseParams.IdentityToken]);
    Entry.TokenType := JStringOrDefault(TokenResponse[TokenResponseParams.TokenType]);
    Entry.Scope := JStringOrDefault(TokenResponse[TokenResponseParams.Scope]);
    if Entry.Scope = '' then
      Entry.Scope := AuthState.Scope;
    ExpiresIn := JIntegerOrDefault(TokenResponse[TokenResponseParams.ExpiresIn]);
    if ExpiresIn = 0 then
      Entry.ExpiresAt := MaxDateTime
    else
      Entry.ExpiresAt := IncSecond(Now, ExpiresIn);
  finally
{$IFNDEF PAS2JS}
    TokenResponse.Free;
{$ENDIF}
  end;
end;

function TOidcClient.ProcessAuthResponse(const CallbackUrl: string): TAuthResult;
var
  AuthState: TAuthState;
  Response: TClientAuthResponse;
  Entry: TAuthResultEntry;
begin
{$IFDEF PAS2JS}
  await(CheckProviderInformation);
{$ELSE}
  CheckProviderInformation;
{$ENDIF}

{$IFNDEF DELPHITOKYO_LVL} // W1035 warning in Seattle and Berlin
  Result := nil;
{$ENDIF}

  // Repeated code from IsValidCallback
  AuthState := FStorage.Get<TAuthState>(AuthStateKey);
  if AuthState = nil then
    raise EOidcClientException.Create(_(SAuthStateNotExpected));
  if not IsCallbackUri(CallbackUrl, AuthState.State) then
    raise EOidcClientException.Create(_(SInvalidCallbackUrl));

  try
    // Retrieve the response
    Response := ResponseFromParams(ParamsFromUri(CallbackUrl));
    try
{$IFDEF PAS2JS}
      Entry := TAuthResultEntry.new;
      await(ValidateResponse(Entry, Response, AuthState));
{$ELSE}
      Entry := TAuthResultEntry.Create;
      try
        ValidateResponse(Entry, Response, AuthState);
      except
        Entry.Free;
        raise;
      end;
{$ENDIF}
    finally
      Response.Free;
    end;

    Result := TAuthResult.Create(Entry);
{$IFDEF PAS2JS}
    // Remove query parameters from URL
    window.history.replaceState(TJSObject.new, document.Title, window.location.pathname);
{$ENDIF}
  finally
    // AuthState finished
    FStorage.Remove(AuthStateKey);
  end;
end;

function TOidcClient.RemoveTrailingSlash(const S: string): string;
begin
  if (S <> '') and (S[Length(S)] = '/') then
    Result := Copy(S, 1, Length(S) - 1)
  else
    Result := S;
end;

function TOidcClient.RequestToken: ITokenResult;
var
  Entry: TAuthResultEntry;
begin
{$IFDEF PAS2JS}
  Entry := TAuthResultEntry.new;
  await(RequestTokenEntry(Entry, Self.Scope));
{$ELSE}
  Entry := TAuthResultEntry.Create;
  try
    RequestTokenEntry(Entry, Self.Scope);
  except
    Entry.Free;
    raise;
  end;
{$ENDIF}
  Result := TAuthResult.Create(Entry);
end;

procedure TOidcClient.RequestTokenEntry(Entry: TAuthResultEntry; const AScope: string);
var
  Params: IModifiableParams;
  AuthHeader: string;
  RequestParams: string;
  TokenResponse: TJObject;
begin
  // build request params
  Params := TModifiableParams.Create;
  Params.Add(TokenRequestParams.GrantType, GrantTypes.ClientCredentials);
  if AScope <> '' then
    Params.Add(TokenRequestParams.Scope, AScope);
  if Self.ClientSecret = '' then // no authentication
  begin
    Params.Add(AuthorizeRequestParams.ClientId, Self.ClientId);
    AuthHeader := '';
  end
  else
    AuthHeader := TSparkleUtils.BasicAuthHeaderValue(Self.ClientId, Self.ClientSecret);
  RequestParams := BuildQueryParams(Params);

{$IFDEF PAS2JS}
  TokenResponse := await(HttpRequestToken(RequestParams, AuthHeader));
{$ELSE}
  TokenResponse := HttpRequestToken(RequestParams, AuthHeader);
{$ENDIF}
  try
    ReadTokenResponse(TokenResponse, Entry, AScope);
  finally
{$IFNDEF PAS2JS}
    TokenResponse.Free;
{$ENDIF}
  end;
  Entry.ClientId := Self.ClientId;
end;

procedure TOidcClient.ReadTokenResponse(TokenResponse: TJObject; Entry: TAuthResultEntry; const AScope: string);
var
  Error: string;
  ExpiresIn: Integer;
begin
  if TokenResponse = nil then
    raise EOidcClientException.Create('Could not retrieve token');

  Error := JStringOrDefault(TokenResponse[TokenResponseParams.Error]);
  if Error <> '' then
    raise EOidcClientError.Create(Error, JStringOrDefault(TokenResponse[TokenResponseParams.ErrorDescription]));

  Entry.AccessToken := JStringOrDefault(TokenResponse[TokenResponseParams.AccessToken]);
  Entry.RefreshToken := JStringOrDefault(TokenResponse[TokenResponseParams.RefreshToken]);
  Entry.IdToken := JStringOrDefault(TokenResponse[TokenResponseParams.IdentityToken]);
  Entry.TokenType := JStringOrDefault(TokenResponse[TokenResponseParams.TokenType]);
  Entry.Scope := JStringOrDefault(TokenResponse[TokenResponseParams.Scope]);
  if Entry.Scope = '' then
    Entry.Scope := AScope;
  ExpiresIn := JIntegerOrDefault(TokenResponse[TokenResponseParams.ExpiresIn]);
  if ExpiresIn = 0 then
    Entry.ExpiresAt := MaxDateTime
  else
    Entry.ExpiresAt := IncSecond(Now, ExpiresIn);
end;

procedure TOidcClient.RefreshTokenEntry(Entry: TAuthResultEntry; const ARefreshToken, AScope: string);
var
  Params: IModifiableParams;
  AuthHeader: string;
  RequestParams: string;
  TokenResponse: TJObject;
begin
  if ARefreshToken = '' then
    raise EOidcClientException.CreateFmt(_(SMissingParameter), [TokenRequestParams.RefreshToken]);

  Params := TModifiableParams.Create;
  Params.Add(TokenRequestParams.GrantType, GrantTypes.RefreshToken);
  Params.Add(TokenRequestParams.RefreshToken, ARefreshToken);
  if AScope <> '' then
    Params.Add(TokenRequestParams.Scope, AScope);
  if Self.ClientSecret = '' then
  begin
    Params.Add(AuthorizeRequestParams.ClientId, Self.ClientId);
    AuthHeader := '';
  end
  else
    AuthHeader := TSparkleUtils.BasicAuthHeaderValue(Self.ClientId, Self.ClientSecret);
  RequestParams := BuildQueryParams(Params);


{$IFDEF PAS2JS}
  TokenResponse := await(HttpRequestToken(RequestParams, AuthHeader));
{$ELSE}
  TokenResponse := HttpRequestToken(RequestParams, AuthHeader);
{$ENDIF}
  try
    ReadTokenResponse(TokenResponse, Entry, AScope);
  finally
{$IFNDEF PAS2JS}
    TokenResponse.Free;
{$ENDIF}
  end;
  Entry.ClientId := Self.ClientId;
  // Use new refresh token if server issued one (rotation), otherwise keep the original
  if Entry.RefreshToken = '' then
    Entry.RefreshToken := ARefreshToken;
end;

function TOidcClient.RefreshTokens(const ARefreshToken: string; const AScope: string = ''): TAuthResult;
var
  Entry: TAuthResultEntry;
begin
{$IFDEF PAS2JS}
  Entry := TAuthResultEntry.new;
  await(CheckProviderInformation);
  await(RefreshTokenEntry(Entry, ARefreshToken, AScope));
{$ELSE}
  CheckProviderInformation;
  Entry := TAuthResultEntry.Create;
  try
    RefreshTokenEntry(Entry, ARefreshToken, AScope);
  except
    Entry.Free;
    raise;
  end;
{$ENDIF}
  Result := TAuthResult.Create(Entry);
end;

function TOidcClient.ResponseFromParams(Params: TQueryString): TClientAuthResponse;
begin
  Result := TClientAuthResponse.Create;
  try
    Result.State := Params.GetValue(AuthorizeResponseParams.State);
    Result.IdToken := Params.GetValue(AuthorizeResponseParams.IdentityToken);
    Result.AccessToken := Params.GetValue(AuthorizeResponseParams.AccessToken);
    Result.TokenType := Params.GetValue(AuthorizeResponseParams.TokenType);
    Result.Scope := Params.GetValue(AuthorizeResponseParams.Scope);
    Result.Code := Params.GetValue(AuthorizeResponseParams.Code);
    Result.ExpiresIn := ExpiresInFromString(Params.GetValue(AuthorizeResponseParams.ExpiresIn));
    Result.Error := Params.GetValue(AuthorizeResponseParams.Error);
    Result.ErrorDescription := Params.GetValue(AuthorizeResponseParams.ErrorDescription);
  except
    Result.Free;
    raise;
  end;
end;

procedure TOidcClient.SetAuthority(const Value: string);
begin
  if FAuthority <> Value then
  begin
    FAuthority := Value;
    FreeAndNil(FValidProviderInfo);
  end;
end;

function TOidcClient.AuthStateKey: string;
begin
  Result := 'st::' + ClientId;
end;

procedure TOidcClient.ValidateAccessToken(Response: TClientAuthResponse; AuthState: TAuthState);
begin
  // TODO: Validate access token (at_hash)
end;

procedure TOidcClient.ValidateIdToken(Entry: TAuthResultEntry; AuthState: TAuthState);
var
  Payload: TJObject;
begin
  if AuthState.Nonce = '' then
    raise EOidcClientException.Create(_(SMissingNonceInState));

  Payload := GetJwtPayload(Entry.IdToken);
  try
    if Payload = nil then
      raise EOidcClientException.Create(_(SIdTokenInvalidPayload));

    if AuthState.Nonce <> JToStr(Payload[JwtClaimNames.Nonce]) then
      raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.Nonce, JToStr(Payload[JwtClaimNames.Nonce])]);

    ValidateIdTokenJwt(Payload, ValidProviderInfo.Issuer, AuthState.ClientId);
  finally
{$IFNDEF PAS2JS}
    Payload.Free;
{$ENDIF}
  end;

  // Todo: validate JWT signature
end;

procedure TOidcClient.DoValidateIssuer(const ExpectedIssuer, TokenIssuer, TenantId: string);
var
  Valid: Boolean;
  ResolvedIssuer: string;
begin
  // Resolve the {tenantid} placeholder used by multi-tenant providers (Microsoft Entra)
  // with the tenant id carried in the token, so the issuer is validated against the
  // concrete per-tenant value instead of being skipped altogether.
  ResolvedIssuer := ExpectedIssuer;
  if (TenantId <> '') and ContainsText(ResolvedIssuer, IssuerTenantPlaceholder) then
    ResolvedIssuer := ReplaceText(ResolvedIssuer, IssuerTenantPlaceholder, TenantId);

  if not FSkipIssuerValidation then
    Valid := RemoveTrailingSlash(ResolvedIssuer) = RemoveTrailingSlash(TokenIssuer)
  else
    Valid := True;

  if Assigned(FOnValidateIssuer) then
    FOnValidateIssuer(TokenIssuer, ResolvedIssuer, Valid);

  if not Valid then
    raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.Issuer, TokenIssuer]);
end;

procedure TOidcClient.ValidateIdTokenJwt(Payload: TJObject; const Issuer, Audience: string);
const
  ClockSkew = 5 * 60;  // 5 minutes
var
  LowerNow, UpperNow: NativeInt;
  NowEpoch: NativeInt;
  IssuedAt: NativeInt;
  Expiration: NativeInt;
  NotBefore: NativeInt;
  Found: Boolean;
  Audiences: TJArray;
  I: Integer;
  AudiencesValue: string;
  TenantId: string;
begin
  if not JIsString(Payload[JwtClaimNames.Subject]) then
    raise EOidcClientException.CreateFmt(_(SMissingClaim), [JwtClaimNames.Subject]);

  if not JIsString(Payload[JwtClaimNames.Issuer]) then
    raise EOidcClientException.CreateFmt(_(SMissingClaim), [JwtClaimNames.Issuer]);

  if JIsString(Payload[JwtClaimNames.TenantId]) then
    TenantId := JToStr(Payload[JwtClaimNames.TenantId])
  else
    TenantId := '';

  DoValidateIssuer(Issuer, JToStr(Payload[JwtClaimNames.Issuer]), TenantId);

  if JIsString(Payload[JwtClaimNames.Audience]) then
  begin
    if Audience <> JToStr(Payload[JwtClaimNames.Audience]) then
      raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.Audience, JToStr(Payload[JwtClaimNames.Audience])]);
  end
  else
  if JIsArray(Payload[JwtClaimNames.Audience]) then
  begin
    Audiences := AsJArray(Payload[JwtClaimNames.Audience]);
    AudiencesValue := '';
    Found := False;
    for I := 0 to Audiences.Length - 1 do
    begin
      if JIsString(Audiences[I]) and (JToStr(Audiences[I]) = Audience) then
      begin
        Found := True;
        Break;
      end;
      if AudiencesValue <> '' then
        AudiencesValue := AudiencesValue + ',';
      AudiencesValue := AudiencesValue + JToStr(Audiences[I]);
    end;
    if not Found then
      raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.Audience, AudiencesValue]);
  end
  else
    raise EOidcClientException.CreateFmt(_(SMissingClaim), [JwtClaimNames.Audience]);

  if JIsString(Payload[JwtClaimNames.AuthorizedParty]) then
    if Audience <> JToStr(Payload[JwtClaimNames.AuthorizedParty]) then
      raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.AuthorizedParty, JToStr(Payload[JwtClaimNames.AuthorizedParty])]);

  // Validate expiration and times
{$IFDEF PAS2JS}
  NowEpoch := TJSDate.Now div 1000;
{$ELSE}
  NowEpoch := DateTimeToUnix(Now, False);
{$ENDIF}
  LowerNow := NowEpoch + ClockSkew;
  UpperNow := NowEpoch - ClockSkew;

  if not JIsInteger(Payload[JwtClaimNames.IssuedAt]) then
    raise EOidcClientException.CreateFmt(_(SMissingClaim), [JwtClaimNames.IssuedAt]);
  IssuedAt := JToInt(Payload[JwtClaimNames.IssuedAt]);
  if LowerNow < IssuedAt then
    raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.IssuedAt, IntToStr(IssuedAt)]);

  if not JIsInteger(Payload[JwtClaimNames.Expiration]) then
    raise EOidcClientException.CreateFmt(_(SMissingClaim), [JwtClaimNames.Expiration]);
  Expiration := JToInt(Payload[JwtClaimNames.Expiration]);
  if Expiration < upperNow then
    raise EOidcClientException.Create(_(SIdTokenExpired));

  if JIsInteger(Payload[JwtClaimNames.NotBefore]) then
  begin
    NotBefore := JToInt(Payload[JwtClaimNames.NotBefore]);
    if LowerNow < NotBefore then
      raise EOidcClientException.CreateFmt(_(SInvalidClaim), [JwtClaimNames.NotBefore, IntToStr(NotBefore)]);
  end;
end;

procedure TOidcClient.ValidateResponse(Entry: TAuthResultEntry; Response: TClientAuthResponse; AuthState: TAuthState);
begin
  if Response.State <> AuthState.State then
    raise EOidcClientException.CreateFmt(_(SInvalidState), [Response.State]);

  if Response.Error <> '' then
    raise EOidcClientError.Create(Response.Error, Response.ErrorDescription);

  if AuthState.ClientId = '' then
    raise EOidcClientException.Create(Format(_(SMissingParameter), [AuthorizeRequestParams.ClientId]));

  if AuthState.ClientId <> Self.ClientId then
    raise EOidcClientException.Create(Format(_(SInvalidParameter), [AuthorizeRequestParams.ClientId, AuthState.ClientId]));

  if Response.Code <> '' then // code flow
{$IFDEF PAS2JS}
    await(ProcessCodeResponse(Entry, Response, AuthState))
{$ELSE}
    ProcessCodeResponse(Entry, Response, AuthState)
{$ENDIF}
  else
  begin
    if (AuthState.CodeVerifier <> '') then
      raise EOidcClientException.Create(Format(_(SMissingParameter), [TokenRequestParams.CodeVerifier]));

    Entry.AccessToken := Response.AccessToken;
    Entry.IdToken := Response.IdToken;
    Entry.TokenType := Response.TokenType;
    if Response.Scope <> '' then
      Entry.Scope := Response.Scope
    else
      Entry.Scope := AuthState.Scope;
    if Response.ExpiresIn = 0 then
      Entry.ExpiresAt := MaxDateTime
    else
      Entry.ExpiresAt := IncSecond(Now, Response.ExpiresIn);
  end;
  Entry.ClientId := Self.ClientId;
  Entry.AppState := AuthState.AppState;

  if (AuthState.Nonce <> '') and (Entry.IdToken = '') then
    raise EOidcClientException.Create(Format(_(SMissingParameter), [AuthorizeResponseParams.IdentityToken]));

  if (AuthState.Nonce = '') and (Entry.IdToken <> '') then
    raise EOidcClientException.Create(Format(_(SMissingParameter), [AuthorizeRequestParams.Nonce]));

  { Validate tokens }
  if Entry.IdToken <> '' then
  begin
    ValidateIdToken(Entry, AuthState);
    if Entry.AccessToken <> '' then
      ValidateAccessToken(Response, AuthState);
  end;
end;

{ EOidcClientError }

constructor EOidcClientError.Create(const Error, ErrorDescription: string);
begin
  if ErrorDescription <> '' then
    inherited CreateFmt(_(SOAuthError), [Error, ErrorDescription])
  else
    inherited CreateFmt(_(SOAuthErrorWithoutDescription), [Error]);
end;

end.
